import React, { useContext, useMemo, useCallback } from 'react'
import { useIntl } from 'react-intl'
import Clickable from 'components/Common/Clickable'
import TextButton from 'LuxKit/Button/TextButton'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'

import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { arrayToObject } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { pushWithRegion } from '../../../../actions/NavigationActions'

const EditContainer = styled(Clickable)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-inline: ${rem(24)};

  &.map-view {
    background-color: ${props => props.theme.palette.neutral.default.eight};
    display: none;

    ${mediaQueryDown.tablet} {
      display: flex;
    }
  }
`

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  row-gap: ${rem(2)};

  &.map-view {
    align-items: center;
  }
`

const EndContainers = styled.div`
  width: ${rem(40)};
  display: flex;
  justify-content: center;
  align-items: center;
`

interface Props {
  className?: string
  isMapView?: boolean
  location?: string
  searchModeUrl?: string
}

function ExperienceSearchBar({ className, isMapView = false, location, searchModeUrl }: Props) {
  const intl = useIntl()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const dispatch = useAppDispatch()

  const openSearchDrawer = useCallback(() => { toggleSearchVertical('experiences') }, [toggleSearchVertical])
  const categories = useAppSelector(state => state.experience.experienceCategories)
  const { searchItem, experienceCategories } = useContext(GlobalSearchStateContext)

  const selectedCategoryNames = useMemo(() => {
    const categoriesById = arrayToObject(categories, cat => cat.id)
    return experienceCategories?.map(id => categoriesById[id]?.name).filter(Boolean).join(', ')
  }, [categories, experienceCategories])

  const handleBackToListClick = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
    if (searchModeUrl) {
      dispatch(pushWithRegion(searchModeUrl))
    }
  }, [dispatch, searchModeUrl])

  if (isMapView) {
    return (
      <EditContainer className={`${className} map-view`}>
        <EndContainers onClick={handleBackToListClick}>
          <LineArrowLeftIcon />
        </EndContainers>
        <DetailsSection className="map-view">
          <Heading variant="heading6">
            {location ?? intl.formatMessage({
              id: 'M4AW+ok4Tg',
              defaultMessage: 'Map area',
              description: 'Default heading when no location is selected in experience search map view',
            })}
          </Heading>
          <Caption variant="large">
            {intl.formatMessage({
              id: 'N7sarHfF3H',
              defaultMessage: 'Experiences',
              description: 'Label indicating the experience search vertical in map view search bar',
            })}
          </Caption>
        </DetailsSection>
        <EndContainers onClick={openSearchDrawer}>
          <TextButton kind="tertiary" nonInteractable>
            {intl.formatMessage({
              id: 'uOEvgIOTWV',
              defaultMessage: 'Edit',
              description: 'Button text to edit search criteria in experience search bar',
            })}
          </TextButton>
        </EndContainers>
      </EditContainer>
    )
  }

  return <EditContainer onClick={openSearchDrawer} className={className}>
    <DetailsSection>
      {searchItem && <Heading variant="heading6">{searchItem.format.mainText}</Heading>}
      {!!experienceCategories?.length && <Caption wrap="truncate" variant="large">
        {selectedCategoryNames}
      </Caption>}
    </DetailsSection>
    <TextButton kind="tertiary" endIcon={<LineEditAltIcon />} nonInteractable>
      {intl.formatMessage({
        id: 'uOEvgIOTWV',
        defaultMessage: 'Edit',
        description: 'Button text to edit search criteria in experience search bar',
      })}
    </TextButton>
  </EditContainer>
}

export default ExperienceSearchBar
